import { Component, ErrorInfo, ReactNode } from "react";
import { isBenignDomError } from "../utils/errorHandler";
import ErrorPage from "./errorPage";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode | ((error: Error, resetError: () => void) => ReactNode);
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    if (isBenignDomError(error)) {
      return { hasError: false, error: undefined };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // 错误上报逻辑
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    } else {
      // 使用指定的错误日志前缀
      console.error("Application error:", error, errorInfo);
    }
  }

  resetError = (): void => {
    this.setState({ hasError: false, error: undefined });
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    if (hasError && error) {
      if (typeof fallback === "function") {
        return fallback(error, this.resetError);
      }

      return (
        fallback ||
        ErrorPage({
          errorMsg: "",
          canRetry: true,
        })
      );
    }

    return children;
  }
}

export default ErrorBoundary;
